import type { IntegrationPlatform } from '../../services/integrationApi';
import type { OrgFeatureFlagKey } from '../../utils/orgFeatureFlagKeys';

/**
 * Which steps the post-connect walkthrough shows for an integration. Pure, so
 * the platform / flag / permission combinations can be unit-tested without React.
 */

export type WalkthroughStepId =
  | 'catalog-match'
  | 'sync-mode'
  | 'data-sync'
  | 'products'
  | 'categories'
  | 'rebates'
  | 'zones'
  | 'automation'
  | 'finish';

export interface WalkthroughScopeLocks {
  /** Shopify `write_discounts` is not granted: the rebate discount switches cannot work until reconnect. */
  writeDiscounts?: boolean;
}

export interface WalkthroughRebatesStep {
  /** Ask the store-level checkout treatment (flag `rebate_checkout_treatment`). */
  treatment: boolean;
  /** Show the instant-rebate discount switch (flag `shopify_rebate_discounts`). */
  instant: boolean;
  /** Show the bundle-rebate discount switch (flag `bundle_shopify_discounts`). */
  bundle: boolean;
  /** Show the reconnect note instead of the discount switches. */
  discountsLocked: boolean;
  /** Show the Lightspeed register-discount switch (flags `lightspeed_sales_sync` + `lightspeed_store_config`). */
  lightspeedInstant?: boolean;
}

export interface WalkthroughStep {
  id: WalkthroughStepId;
  /** Key under `integrations.setupWalkthrough.steps` (label, title and body live there). */
  i18nKey: 'catalogMatch' | 'catalogMatchShopify' | 'catalogMatchEcom' | 'syncMode' | 'dataSync' | 'products' | 'categories' | 'rebates' | 'zones' | 'automation' | 'finish';
  /** Sync modes the step offers (`sync-mode` only). */
  modes?: Array<'link_only' | 'enrich' | 'override'>;
  /** What the `rebates` step shows (`rebates` only). */
  rebates?: WalkthroughRebatesStep;
}

export interface WalkthroughStepsInput {
  platform: IntegrationPlatform;
  flags: Partial<Record<OrgFeatureFlagKey, boolean>>;
  scopeLocks?: WalkthroughScopeLocks;
}

/** Lightspeed sync modes for the settings drawer and the setup walkthrough. Null when neither flag is on. */
export function lightspeedCatalogModes(
  flags: Partial<Record<'lightspeed_link_only' | 'lightspeed_catalog_parity', boolean>>,
): Array<'link_only' | 'enrich' | 'override'> | null {
  if (flags.lightspeed_link_only !== true && flags.lightspeed_catalog_parity !== true) return null;
  const modes: Array<'link_only' | 'enrich' | 'override'> = [];
  if (flags.lightspeed_link_only === true) modes.push('link_only');
  if (flags.lightspeed_catalog_parity === true) modes.push('enrich');
  modes.push('override');
  return modes;
}

export function walkthroughStepsFor({ platform, flags, scopeLocks }: WalkthroughStepsInput): WalkthroughStep[] {
  const isShopify = platform === 'shopify';
  const steps: WalkthroughStep[] = [];

  // A Lightspeed store usually already has its catalog: match it first, so the
  // sync mode and product choices that follow are made against real matches.
  // The scan itself stays behind lightspeed_link_only.
  if (platform === 'lightspeed' && flags.lightspeed_link_only === true) {
    steps.push({ id: 'catalog-match', i18nKey: 'catalogMatch' });
  }

  // Sync mode is a Shopify setting (link only / enrich / override). Lightspeed
  // offers link only behind lightspeed_link_only and fill-gaps behind
  // lightspeed_catalog_parity. BigCommerce and WooCommerce have no mode.
  const lightspeedModes = platform === 'lightspeed' ? lightspeedCatalogModes(flags) : null;
  if (isShopify) {
    steps.push({ id: 'sync-mode', i18nKey: 'syncMode', modes: ['link_only', 'enrich', 'override'] });
  } else if (lightspeedModes) {
    steps.push({ id: 'sync-mode', i18nKey: 'syncMode', modes: lightspeedModes });
  }

  // Lightspeed eCom (flag `lightspeed_ecom_connector`) is content-only: its data step shows the
  // eCom push fields (content, description, tags, metafields, visibility) instead of the generic
  // products / pricing / images / inventory switches, which it never reads.
  steps.push({ id: 'data-sync', i18nKey: 'dataSync' });

  // With `shopify_scan_before_selection` the scan covers the store's brands, so a new
  // Shopify store scans first and adds its matches; Choose products then fills the rest.
  const scanFirst = isShopify && flags.shopify_scan_before_selection === true;
  if (scanFirst) steps.push({ id: 'catalog-match', i18nKey: 'catalogMatchShopify' });
  steps.push({ id: 'products', i18nKey: 'products' });

  // Lightspeed store configuration: categories and custom fields, then rebates at the register.
  const lightspeedConfig = platform === 'lightspeed' && flags.lightspeed_store_config === true;
  if (lightspeedConfig) {
    steps.push({ id: 'categories', i18nKey: 'categories' });
    const lightspeedInstant = flags.lightspeed_sales_sync === true;
    if (flags.rebate_checkout_treatment === true || lightspeedInstant) {
      steps.push({
        id: 'rebates',
        i18nKey: 'rebates',
        rebates: {
          treatment: flags.rebate_checkout_treatment === true,
          instant: false,
          bundle: false,
          discountsLocked: false,
          lightspeedInstant,
        },
      });
    }
  }

  if (
    isShopify &&
    (flags.rebate_checkout_treatment === true ||
      flags.shopify_rebate_discounts === true ||
      flags.bundle_shopify_discounts === true)
  ) {
    steps.push({
      id: 'rebates',
      i18nKey: 'rebates',
      rebates: {
        treatment: flags.rebate_checkout_treatment === true,
        instant: flags.shopify_rebate_discounts === true,
        bundle: flags.bundle_shopify_discounts === true,
        discountsLocked: scopeLocks?.writeDiscounts === true,
      },
    });
  }

  if (isShopify && flags.shopify_markets_pricing === true) {
    steps.push({ id: 'zones', i18nKey: 'zones' });
  }

  steps.push({ id: 'automation', i18nKey: 'automation' });

  // Without the flag the scan only sees the selection, so it runs last, once the
  // mode and the product selection are decided.
  if (isShopify && !scanFirst) {
    steps.push({ id: 'catalog-match', i18nKey: 'catalogMatchShopify' });
  }
  // The eCom scan matches the shop's variants against the selection, so it also runs last.
  if (platform === 'lightspeed_ecom') {
    steps.push({ id: 'catalog-match', i18nKey: 'catalogMatchEcom' });
  }
  steps.push({ id: 'finish', i18nKey: 'finish' });
  return steps;
}
